<template>
  <div class="event-var-container">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner">
        <font-awesome-icon icon="running" class="fa-3x running-animate" />
        <p>{{ $t('eventVar.loading') || 'Loading...' }}</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-card">
        <font-awesome-icon icon="exclamation-triangle" class="error-icon" />
        <h3>{{ $t('eventVar.error') }}</h3>
        <p>{{ error }}</p>
        <button @click="loadData" class="retry-btn">
          <font-awesome-icon icon="redo" />
          {{ $t('eventVar.retry') }}
        </button>
      </div>
    </div>

    <!-- Data Display -->
    <div v-else-if="data" class="data-container">
      <!-- Large Screen Recommendation Popup -->
      <div v-if="showLargeScreenPopup && isMobileDevice" class="popup-overlay" @click="closeLargeScreenPopup">
        <div class="popup-content" @click.stop>
          <div class="popup-header">
            <font-awesome-icon icon="desktop" class="popup-icon" />
            <h3>{{ $t('eventVar.largeScreenTitle') }}</h3>
            <button @click="closeLargeScreenPopup" class="popup-close-btn">
              <font-awesome-icon icon="times" />
            </button>
          </div>
          <div class="popup-body">
            <p>{{ $t('eventVar.largeScreenMessage') }}</p>
            <div class="popup-features">
              <div class="feature-item">
                <font-awesome-icon icon="table" class="feature-icon" />
                <span>{{ $t('eventVar.featureTable') }}</span>
              </div>
              <div class="feature-item">
                <font-awesome-icon icon="search" class="feature-icon" />
                <span>{{ $t('eventVar.featureFilters') }}</span>
              </div>
              <div class="feature-item">
                <font-awesome-icon icon="download" class="feature-icon" />
                <span>{{ $t('eventVar.featureExport') }}</span>
              </div>
            </div>
          </div>
          <div class="popup-footer">
            <button @click="closeLargeScreenPopup" class="popup-continue-btn">
              {{ $t('eventVar.continueAnyway') }}
            </button>
          </div>
        </div>
      </div>
      <!-- Header Section -->
      <div class="header-section">
        <div class="header-card">
          <div class="header-content">
            <div class="event-info">
              <h1 class="event-title">
                <font-awesome-icon icon="chart-line" class="title-icon" />
                {{ data.event_name }}
              </h1>
              <div class="event-meta">
                <span class="date-range">
                  <font-awesome-icon icon="calendar-alt" class="meta-icon" />
                  {{ data.date_range.start_date }} - {{ data.date_range.end_date }}
                </span>
                <span class="total-athletes">
                  <font-awesome-icon icon="users" class="meta-icon" />
                  {{ $t('eventVar.totalAthletes', { count: data.total_athletes }) }}
                </span>
              </div>
            </div>
            <div class="header-actions">
              <router-link
                v-if="availableTeams.length > 0"
                :to="{ name: 'teamEventDashboard', params: { slug: data.event_slug } }"
                class="team-dashboard-btn"
                :title="$t('eventVar.teamDashboard')"
              >
                <font-awesome-icon icon="users" />
                <span v-if="!isMobileDevice">{{ $t('eventVar.teamDashboard') }}</span>
              </router-link>
              <router-link
                v-else
                :to="{ name: 'eventDashboard', params: { slug: data.event_slug } }"
                class="team-dashboard-btn"
                :title="$t('eventVar.eventDashboard')"
              >
                <font-awesome-icon icon="tachometer-alt" />
                <span v-if="!isMobileDevice">{{ $t('eventVar.eventDashboard') || 'Event Dashboard' }}</span>
              </router-link>
              <button @click="exportToCSV" class="export-btn" :title="$t('eventVar.exportCSV')">
                <font-awesome-icon icon="download" />
                <span v-if="!isMobileDevice">{{ $t('eventVar.export') }}</span>
              </button>
              <button @click="refreshData" class="refresh-btn" :title="$t('eventVar.refresh')">
                <font-awesome-icon icon="sync" />
                <span v-if="!isMobileDevice">{{ $t('eventVar.refresh') }}</span>
              </button>
              <button @click="runInspection" class="export-btn" :title="$t('eventVar.inspection')">
                <font-awesome-icon icon="search" />
                <span v-if="!isMobileDevice">{{ $t('eventVar.inspection') || 'Var' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="filters-section">
        <div class="filters-card">
          <div class="filters-content">
            <!-- Search Filter -->
            <div class="search-wrapper">
              <input
                v-model="searchTerm"
                :placeholder="$t('eventVar.searchPlaceholder')"
                class="search-input"
                @input="filterData"
              />
              <font-awesome-icon icon="search" class="search-icon" />
            </div>

            <!-- Team Filter -->
            <div v-if="availableTeams.length > 0" class="filter-wrapper">
              <select v-model="selectedTeam" @change="filterData" class="team-select">
                <option value="">{{ $t('eventVar.allTeams') }}</option>
                <option v-for="team in availableTeams" :key="team" :value="team">
                  {{ team }}
                </option>
              </select>
            </div>

            <!-- Gender Filter -->
            <div class="filter-wrapper">
              <select v-model="selectedGender" @change="filterData" class="gender-select">
                <option value="">{{ $t('eventVar.allGenders') }}</option>
                <option value="M">{{ $t('eventVar.male') }}</option>
                <option value="F">{{ $t('eventVar.female') }}</option>
              </select>
            </div>

            <!-- Activity Type Filter -->
            <div v-if="availableActivityTypes.length > 1" class="filter-wrapper">
              <select v-model="selectedActivityType" @change="filterData" class="activity-type-select">
                <option value="">{{ $t('eventVar.allActivityTypes') || 'All Types' }}</option>
                <option v-for="activityType in availableActivityTypes" :key="activityType" :value="activityType">
                  {{ activityType }}
                </option>
              </select>
            </div>

            <!-- Clear Filters -->
            <button v-if="hasActiveFilters" @click="clearFilters" class="clear-filters-btn">
              <font-awesome-icon icon="times" />
              {{ $t('eventVar.clearFilters') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Table Section -->
      <div class="table-section">
        <div class="table-card">
          <div class="table-header">
            <h2 class="table-title">
              <font-awesome-icon icon="table" class="table-icon" />
              {{ $t('eventVar.teamStats') }}
            </h2>
            <div class="table-info">
              <span class="showing-count">
                {{ $t('eventVar.showing', { count: filteredData.length, total: data.total_athletes }) }}
              </span>
            </div>
          </div>

          <!-- Desktop Table -->
          <div class="table-container desktop-table">
            <table class="stats-table">
              <thead>
                <tr>
                  <th class="rank-col">{{ $t('eventVar.rank') }}</th>
                  <th class="athlete-col">{{ $t('eventVar.athlete') }}</th>
                  <th class="gender-col">{{ $t('eventVar.gender') }}</th>
                  <th class="team-col">{{ $t('eventVar.team') }}</th>
                  <th
                    v-for="dateGroup in dateColumns"
                    :key="dateGroup.date"
                    :colspan="dateGroup.columns.length"
                    class="date-group-header"
                  >
                    {{ formatDate(dateGroup.date) }}
                  </th>
                </tr>
                <!-- Activity type sub-headers -->
                <tr>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <template v-for="dateGroup in dateColumns">
                    <th
                      v-for="column in dateGroup.columns"
                      :key="`${dateGroup.date}-${column.header}`"
                      class="activity-type-header"
                      :data-type="column.activityType"
                    >
                      {{ column.activityType }}
                    </th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in paginatedData" :key="row.strava_id" class="data-row">
                  <td class="rank-cell">{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                  <td class="athlete-cell">
                    <div class="athlete-info">
                      <a
                        :href="`https://www.strava.com/athletes/${row.strava_id}`"
                        target="_blank"
                        class="athlete-name-link"
                      >
                        {{ row.name }}
                      </a>
                      <span class="athlete-id">#{{ row.strava_id }}</span>
                    </div>
                  </td>
                  <td class="gender-cell">
                    <span class="gender-badge" :class="row.gender">
                      <font-awesome-icon :icon="row.gender === 'M' ? 'mars' : 'venus'" />
                      {{ row.gender }}
                    </span>
                  </td>
                  <td class="team-cell">
                    <span v-if="row.team" class="team-badge">
                      <font-awesome-icon icon="users" class="team-icon" />
                      {{ row.team }}
                    </span>
                    <span v-else class="no-team">-</span>
                  </td>
                  <template v-for="dateGroup in dateColumns">
                    <td
                      v-for="column in dateGroup.columns"
                      :key="`${dateGroup.date}-${column.header}`"
                      class="date-cell activity-type-cell"
                      :class="{ 'has-activity': hasActivityValue(row[column.header]) }"
                    >
                      <div v-if="row[column.header] !== null && row[column.header] !== undefined" class="activity-data">
                        <template v-if="typeof row[column.header] === 'object'">
                          <div class="main-value">
                            {{ Number(row[column.header].point || 0).toFixed(0) }}
                            <span class="unit">pt</span>
                          </div>
                          <div class="additional-info">
                            <div v-if="row[column.header].km > 0" class="km-info">
                              {{ Number(row[column.header].km).toFixed(1) }}km
                            </div>
                            <div v-if="row[column.header].moving_time > 0" class="time-info">
                              {{ formatMovingTime(row[column.header].moving_time) }}
                            </div>
                          </div>
                        </template>
                        <template v-else>
                          <div class="main-value">
                            {{ Number(row[column.header]).toFixed(2) }}
                          </div>
                        </template>
                      </div>
                      <span v-else class="no-activity">-</span>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile Cards -->
          <div class="mobile-cards">
            <div v-for="row in paginatedData" :key="row.strava_id" class="athlete-card">
              <div class="card-header">
                <div class="athlete-info">
                  <h3 class="athlete-name">
                    <a
                      :href="`https://www.strava.com/athletes/${row.strava_id}`"
                      target="_blank"
                      class="athlete-name-link"
                    >
                      {{ row.name }}
                    </a>
                  </h3>
                </div>
                <div class="athlete-meta">
                  <span class="gender-badge" :class="row.gender">
                    <font-awesome-icon :icon="row.gender === 'M' ? 'mars' : 'venus'" />
                    {{ row.gender }}
                  </span>
                  <span v-if="row.team" class="team-badge">
                    <font-awesome-icon icon="users" class="team-icon" />
                    {{ row.team }}
                  </span>
                </div>
              </div>
              <div class="card-content">
                <div class="activities-grid">
                  <div
                    v-for="dateGroup in dateColumns"
                    :key="dateGroup.date"
                    class="date-group-mobile"
                  >
                    <div class="date-header-mobile">{{ formatDate(dateGroup.date) }}</div>
                    <div class="activity-types-mobile">
                      <div
                        v-for="column in dateGroup.columns"
                        :key="column.header"
                        class="activity-item activity-type-item"
                        :class="{ 'has-activity': hasActivityValue(row[column.header]) }"
                      >
                        <span class="activity-type-label">{{ column.activityType }}</span>
                        <div v-if="row[column.header] !== null && row[column.header] !== undefined" class="activity-data-mobile">
                          <template v-if="typeof row[column.header] === 'object'">
                            <div class="main-value-mobile">
                              {{ Number(row[column.header].point || 0).toFixed(0) }}pt
                            </div>
                            <div v-if="row[column.header].km > 0" class="km-info-mobile">
                              {{ Number(row[column.header].km).toFixed(1) }}km
                            </div>
                            <div v-if="row[column.header].moving_time > 0" class="time-info-mobile">
                              {{ formatMovingTime(row[column.header].moving_time) }}
                            </div>
                          </template>
                          <template v-else>
                            <div class="main-value-mobile">
                              {{ Number(row[column.header]).toFixed(2) }}
                            </div>
                          </template>
                        </div>
                        <span v-else class="no-activity">-</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="pagination-section">
            <div class="pagination-controls">
              <button
                @click="changePage(currentPage - 1)"
                :disabled="currentPage === 1"
                class="pagination-btn"
              >
                <font-awesome-icon icon="chevron-left" />
                {{ $t('eventVar.previous') }}
              </button>

              <div class="page-numbers">
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  @click="changePage(page)"
                  :class="['page-btn', { active: page === currentPage }]"
                >
                  {{ page }}
                </button>
              </div>

              <button
                @click="changePage(currentPage + 1)"
                :disabled="currentPage === totalPages"
                class="pagination-btn"
              >
                {{ $t('eventVar.next') }}
                <font-awesome-icon icon="chevron-right" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Inspection Results Modal -->
      <div v-if="showInspectionSection" class="modal-overlay" @click.self="!inspectionLoading && (showInspectionSection = false)">
        <div class="modal-content inspection-modal-content compact-inspection-modal">
          <div class="inspection-modal-header">
            <h2 class="inspection-title compact-title">
              <font-awesome-icon icon="search" class="inspection-icon" />
              {{ $t('eventVar.inspectionResults') || 'Inspection Results' }}
            </h2>
            <button @click="!inspectionLoading && (showInspectionSection = false)" :disabled="inspectionLoading" class="inspection-close-btn improved compact-close" title="Close" aria-label="Close">&times;</button>
          </div>
          <div class="inspection-subtitle compact-subtitle">{{$t('eventVar.inspectionSubtitle')}}</div>
          <div v-if="inspectionLoading">
            <div class="loading-spinner" style="text-align:center; margin:1rem 0;">
              <font-awesome-icon icon="spinner" spin class="spinner-icon" />
              <span style="display:block; margin-top:0.5rem; font-size:0.95em;">{{ $t('eventVar.inspectionLoading') }}</span>
            </div>
          </div>
          <div v-else-if="inspectionResult && inspectionResult.count === 0">
            <p style="font-size:0.98em; margin:0.5em 0;">{{ $t('eventVar.noSimilarActivities') || 'No similar activities found.' }}</p>
          </div>
          <div v-else-if="inspectionResult">
            <!-- Showing X-Y of Z groups -->
            <div style="margin-bottom:0.7em; font-size:0.98em; color:#444;">
              {{ $t('eventVar.showingGroups', {
                from: inspectionFromRange,
                to: inspectionToRange,
                total: inspectionTotal
              }) }}
            </div>
            <div v-for="(group, idx) in inspectionResult.data" :key="idx" class="inspection-group improved-group compact-group">
              <div class="compact-group-flex">
                <div v-if="getGroupPolylines(group).length" class="compact-svg-legend">
                  <svg
                    ref="svgMap"
                    :width="'100%'"
                    :height="svgHeight"
                    :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
                    class="compact-svg impressive-svg"
                    style="background: none; border-radius: 8px; border: 1.5px solid #bbb; display:block; cursor: grab; margin-bottom:0.3rem; box-shadow: 0 4px 24px rgba(60,60,120,0.10);"
                    xmlns="http://www.w3.org/2000/svg"
                    @mousedown="startPan($event, idx)"
                    @mousemove="onMouseMove($event, idx)"
                    @mouseup="endPan(idx)"
                    @mouseleave="endPan(idx)"
                    @wheel.prevent="onWheel($event, idx)"
                  >
                    <defs>
                      <!-- Gradient background -->
                      <linearGradient id="svgBgGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stop-color="#e3f0fc"/>
                        <stop offset="100%" stop-color="#f8f9fa"/>
                      </linearGradient>
                      <!-- Polyline shadow/glow -->
                      <filter id="polyGlow" x="-30%" y="-30%" width="160%" height="160%">
                        <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#1976d2" flood-opacity="0.18"/>
                        <feDropShadow dx="0" dy="0" stdDeviation="6" flood-color="#fff" flood-opacity="0.12"/>
                      </filter>
                      <!-- Marker for start/end -->
                      <radialGradient id="markerGradient" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stop-color="#fff"/>
                        <stop offset="100%" stop-color="#1976d2"/>
                      </radialGradient>
                    </defs>
                    <!-- Gradient background rect -->
                    <rect x="0" y="0" :width="svgWidth" :height="svgHeight*0.7" fill="url(#svgBgGradient)" rx="8"/>
                    <!-- Enhanced grid -->
                    <g>
                      <g v-for="i in 5" :key="'h'+i">
                        <line :x1="0" :y1="i*20" :x2="svgWidth" :y2="i*20" stroke="#e0e0e0" stroke-width="1" stroke-dasharray="4 2" />
                      </g>
                      <g v-for="i in 5" :key="'v'+i">
                        <line :x1="i*60" :y1="0" :x2="i*60" :y2="svgHeight*0.7" stroke="#e0e0e0" stroke-width="1" stroke-dasharray="4 2" />
                      </g>
                    </g>
                    <!-- Polylines with animation, shadow, and markers -->
                    <g v-for="(points, pidx) in polylinesToSVGPoints(getGroupPolylines(group), idx, svgWidth, svgHeight*0.7)" :key="pidx">
                      <polyline
                        :points="points"
                        fill="none"
                        :stroke="polylineColors[pidx % polylineColors.length]"
                        :stroke-width="hoveredPolyline[idx] === pidx ? 7 : 4.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        filter="url(#polyGlow)"
                        :style="{ 'strokeDasharray': polylineLengths(points), 'strokeDashoffset': animated ? 0 : polylineLengths(points), 'animation': animated ? 'draw 1.2s cubic-bezier(0.4,0.2,0.2,1) forwards' : 'none', 'cursor': 'pointer', 'transition': 'stroke-width 0.2s' }"
                        opacity="0.97"
                        @mouseenter="showTooltip($event, group, pidx, idx)"
                        @mouseleave="hideTooltip(idx)"
                        @mousemove="moveTooltip($event, idx)"
                      />
                      <circle
                        v-if="points.split(' ').length > 0"
                        :cx="points.split(' ')[0].split(',')[0]"
                        :cy="points.split(' ')[0].split(',')[1]"
                        r="6"
                        fill="url(#markerGradient)"
                        stroke="#fff"
                        stroke-width="2"
                        opacity="0.95"
                        style="filter: drop-shadow(0 2px 4px #1976d2aa);"
                      />
                      <circle
                        v-if="points.split(' ').length > 1"
                        :cx="points.split(' ')[points.split(' ').length-1].split(',')[0]"
                        :cy="points.split(' ')[points.split(' ').length-1].split(',')[1]"
                        r="5"
                        fill="#fff"
                        stroke="orange"
                        stroke-width="2"
                        opacity="0.95"
                        style="filter: drop-shadow(0 2px 4px #ff9800aa);"
                      />
                    </g>
                  </svg>
                  <!-- Tooltip -->
                  <div v-if="tooltip[idx] && tooltip[idx].visible" :style="{ position: 'absolute', left: tooltip[idx].x + 'px', top: tooltip[idx].y + 'px', background: 'rgba(255,255,255,0.98)', border: '1.5px solid #1976d2', borderRadius: '8px', padding: '7px 13px', pointerEvents: 'none', boxShadow: '0 4px 16px rgba(25,118,210,0.13)', zIndex: 10, fontSize: '1em', color: '#222', minWidth: '120px', fontWeight: 500, lineHeight: 1.4 }">
                    <div style="font-size:1.05em; font-weight:600; margin-bottom:2px; color:#1976d2; display:flex; align-items:center; gap:0.4em;">
                      <svg width="12" height="12" style="margin-right:3px;"><circle cx="6" cy="6" r="6" fill="#1976d2"/></svg>
                      Athlete: <span style="margin-left:0.3em;">{{ tooltip[idx].athleteId }}</span>
                    </div>
                    <div><b>Dist:</b> {{ tooltip[idx].distance }} km</div>
                  </div>
                  <!-- Legend -->
                  <div class="polyline-legend compact-legend">
                    <span v-for="(act, pidx) in group" v-if="act.map && act.map.summary_polyline" :key="'legend-'+pidx" class="compact-legend-item">
                      <span :style="{width:'14px',height:'5px',background:polylineColors[pidx % polylineColors.length],display:'inline-block',marginRight:'4px',borderRadius:'2px'}"></span>
                      {{ act.athlete_id }}
                    </span>
                  </div>
                </div>
                <div class="compact-group-title-table">
                  <h3 class="inspection-group-title compact-group-title">
                    Group {{ idx + 1 }} <span class="group-size">({{ group.length }})</span>
                  </h3>
                  <!-- Transposed inspection table -->
                  <table class="inspection-table improved-table compact-table vertical-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th v-for="(act, aidx) in group" :key="'col-'+aidx" class="compact-header" style="text-align:center;">
                          <span v-if="act.id">
                            <a :href="`https://www.strava.com/activities/${act.id}`" target="_blank" rel="noopener" style="color:#e53935;text-decoration:underline;">
                              {{ act.id }}
                            </a>
                          </span>
                          <span v-else>-</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, rIdx) in getTransposedInspectionRows(group)" :key="'row-'+rIdx">
                        <th class="compact-header" style="text-align:left;">{{ row[0] }}</th>
                        <td v-for="(cell, cIdx) in row.slice(1)" :key="'cell-'+cIdx">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <!-- Inspection Pagination Controls -->
            <div v-if="inspectionTotal > inspectionPageSize" class="pagination-section">
              <div class="pagination-controls" style="flex-wrap:wrap; gap:0.7em; align-items:center;">
                <!-- Page size selector -->
                <label style="font-size:0.97em;">
                  {{ $t('eventVar.pageSize') || 'Page size:' }}
                  <select :value="inspectionPageSize" @change="onInspectionPageSizeChange" :disabled="inspectionLoading" style="margin-left:0.4em;">
                    <option v-for="size in inspectionPageSizeOptions" :key="size" :value="size">{{ size }}</option>
                  </select>
                </label>
                <!-- Prev button -->
                <button
                  @click="runInspection(inspectionPage - 1, inspectionPageSize)"
                  :disabled="inspectionPage === 1 || inspectionLoading"
                  class="pagination-btn"
                >
                  <font-awesome-icon icon="chevron-left" />
                  {{ $t('eventVar.previous') }}
                </button>
                <!-- Page info -->
                <span style="margin: 0 1em; font-weight: 500;">
                  {{ inspectionPage }} / {{ Math.ceil(inspectionTotal / inspectionPageSize) }}
                </span>
                <!-- Next button -->
                <button
                  @click="runInspection(inspectionPage + 1, inspectionPageSize)"
                  :disabled="inspectionPage >= Math.ceil(inspectionTotal / inspectionPageSize) || inspectionLoading"
                  class="pagination-btn"
                >
                  {{ $t('eventVar.next') }}
                  <font-awesome-icon icon="chevron-right" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $backend from '../backend'
import polyline from 'polyline-encoded'

export default {
  name: 'EventVar',
  metaInfo () {
    return {
      title: this.title
    }
  },
  data () {
    return {
      title: 'Event Var',
      loading: true,
      error: null,
      data: null,
      searchTerm: '',
      selectedTeam: '',
      selectedGender: '',
      selectedActivityType: '', // New filter for activity type
      currentPage: 1,
      pageSize: 20,
      filteredData: [],
      showLargeScreenPopup: false,
      inspectionResult: null,
      inspectionLoading: false,
      showInspectionSection: false,
      animated: true,
      polylineColors: [
        '#1976d2', '#e53935', '#43a047', '#fbc02d', '#8e24aa', '#00897b', '#6d4c41',
        '#3949ab', '#d81b60', '#00acc1', '#f57c00', '#7cb342', '#c2185b', '#5d4037'
      ],
      svgWidth: 520,
      svgHeight: 260,
      pan: {}, // { [groupIdx]: {x, y, dragging, lastX, lastY} }
      zoom: {}, // { [groupIdx]: scale }
      tooltip: {}, // { [groupIdx]: {visible, x, y, athleteId, distance} }
      hoveredPolyline: {}, // { [groupIdx]: polylineIdx }
      // --- Inspection Pagination ---
      inspectionPage: 1,
      inspectionPageSize: 10,
      inspectionTotal: 0,
      inspectionPageSizeOptions: [5, 10, 20, 50],
      inspectionLastPage: 1,
      inspectionLastPageSize: 10
    }
  },
  computed: {
    dateColumns () {
      if (!this.data || !this.data.headers) return []
      // Always group columns by date since all events now use the same format
      const dateGroups = {}
      this.data.headers.forEach(header => {
        if (header.match(/^\d{4}-\d{2}-\d{2}_/)) {
          const [date, activityType] = header.split('_', 2)
          if (!dateGroups[date]) {
            dateGroups[date] = []
          }
          dateGroups[date].push({ activityType, header })
        }
      })
      return Object.keys(dateGroups).sort().map(date => ({
        date,
        columns: dateGroups[date]
      }))
    },
    availableActivityTypes () {
      if (!this.data || !this.data.event_types) return []
      return this.data.event_types
    },
    availableTeams () {
      if (!this.data || !this.data.rows) return []
      const teams = new Set()
      this.data.rows.forEach(row => {
        if (row.team) teams.add(row.team)
      })
      return Array.from(teams).sort()
    },
    hasActiveFilters () {
      return this.searchTerm || this.selectedTeam || this.selectedGender || this.selectedActivityType
    },
    totalPages () {
      return Math.ceil(this.filteredData.length / this.pageSize)
    },
    visiblePages () {
      const pages = []
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, this.currentPage + 2)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    },
    inspectionFromRange () {
      return isNaN(this.inspectionPage) || isNaN(this.inspectionPageSize) || this.inspectionPage < 1 ? 1 : ((this.inspectionPage - 1) * this.inspectionPageSize) + 1
    },
    inspectionToRange () {
      return isNaN(this.inspectionPage) || isNaN(this.inspectionPageSize) || this.inspectionPage < 1 ? Math.min(this.inspectionPageSize, this.inspectionTotal) : Math.min(this.inspectionPage * this.inspectionPageSize, this.inspectionTotal)
    },
    paginatedData () {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredData.slice(start, end)
    },
    isMobileDevice () {
      return window.innerWidth < 768
    }
  },
  created () {
    this.loadData()
    // Add resize listener for mobile detection
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy () {
    // Clean up resize listener
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async loadData () {
      this.loading = true
      this.error = null

      try {
        const eventSlug = this.$route.params.event_slug
        const response = await $backend.getEventVar(eventSlug)
        this.data = response.data
        this.filteredData = [...this.data.rows]
        this.title = `Event Var - ${this.data.event_name}`
        this.$emit('event-loaded', { name: this.data.event_name })

        // Show large screen recommendation popup
        this.showLargeScreenPopup = true
      } catch (err) {
        // Check if it's an unauthorized error - axios interceptor will handle redirect
        if (err && err.data && err.data.msg === 'unauthorized') {
          // Don't set error message here, let the interceptor handle the redirect
          return
        }
        this.error = (err.data && err.data.msg) || 'Failed to load data'
        console.error('Error loading event var data:', err)
      } finally {
        this.loading = false
      }
    },

    closeLargeScreenPopup () {
      this.showLargeScreenPopup = false
    },

    handleResize () {
      // Force reactivity update for mobile detection
      this.$forceUpdate()
    },

    filterData () {
      if (!this.data || !this.data.rows) return

      this.filteredData = this.data.rows.filter(row => {
        // Search filter
        if (this.searchTerm) {
          const searchLower = this.searchTerm.toLowerCase()
          const nameMatch = row.name.toLowerCase().includes(searchLower)
          const teamMatch = row.team && row.team.toLowerCase().includes(searchLower)
          if (!nameMatch && !teamMatch) return false
        }

        // Team filter
        if (this.selectedTeam && row.team !== this.selectedTeam) {
          return false
        }

        // Gender filter
        if (this.selectedGender && row.gender !== this.selectedGender) {
          return false
        }

        // Activity type filter
        if (this.selectedActivityType) {
          // Check if athlete has any activity of the selected type
          let hasActivityType = false
          for (const key in row) {
            if (key.endsWith(`_${this.selectedActivityType}`)) {
              const value = row[key]
              if (this.hasActivityValue(value)) {
                hasActivityType = true
                break
              }
            }
          }
          if (!hasActivityType) return false
        }

        return true
      })

      this.currentPage = 1
    },

    clearFilters () {
      this.searchTerm = ''
      this.selectedTeam = ''
      this.selectedGender = ''
      this.selectedActivityType = ''
      this.filterData()
    },

    changePage (page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
      }
    },

    refreshData () {
      this.loadData()
    },

    formatDate (dateString) {
      const date = new Date(dateString)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${month}-${day}`
    },

    escapeCsvCell (value) {
      if (value === null || value === undefined) return ''
      const s = String(value)
      if (/[",\r\n]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`
      }
      return s
    },

    exportToCSV () {
      if (!this.data) return

      const headers = this.data.headers
      const rows = this.filteredData

      let csvContent = headers.map(h => this.escapeCsvCell(h)).join(',') + '\n'

      rows.forEach(row => {
        const rowData = headers.map(header => {
          const value = row[header]
          if (value === null || value === undefined) {
            return ''
          }

          // Handle object values (activity data)
          if (typeof value === 'object') {
            // Format as "point,km,time" or just the main value
            const parts = []
            if (value.point !== undefined && value.point !== null) {
              parts.push(`pt:${Number(value.point).toFixed(0)}`)
            }
            if (value.km !== undefined && value.km !== null && value.km > 0) {
              parts.push(`km:${Number(value.km).toFixed(1)}`)
            }
            if (value.moving_time !== undefined && value.moving_time !== null && value.moving_time > 0) {
              parts.push(`time:${this.formatMovingTime(value.moving_time)}`)
            }
            return this.escapeCsvCell(parts.length > 0 ? parts.join(' ') : '')
          }

          return this.escapeCsvCell(value)
        })
        csvContent += rowData.join(',') + '\n'
      })

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `event_var_${this.data.event_slug}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    async runInspection (page = null, pageSize = null) {
      // Defensive: ignore event objects
      if (page && (page instanceof Event || (typeof PointerEvent !== 'undefined' && page instanceof PointerEvent))) page = null
      if (pageSize && (pageSize instanceof Event || (typeof PointerEvent !== 'undefined' && pageSize instanceof PointerEvent))) pageSize = null
      this.inspectionLoading = true
      this.inspectionResult = null
      this.showInspectionSection = true
      // Use last page/size if not provided
      if (page == null) page = this.inspectionLastPage || 1
      if (pageSize == null) pageSize = this.inspectionLastPageSize || 10
      try {
        const eventSlug = this.data.event_slug
        const limit = pageSize
        const offset = (page - 1) * limit
        const result = await $backend.getEventInspection(eventSlug, limit, offset)
        this.inspectionResult = result
        this.inspectionPage = page
        this.inspectionPageSize = pageSize
        this.inspectionTotal = result.count || 0
        this.inspectionLastPage = page
        this.inspectionLastPageSize = pageSize
      } catch (err) {
        this.inspectionResult = { count: 0, data: [] }
        this.$notify && this.$notify({ type: 'error', text: 'Failed to load inspection results.' })
      } finally {
        this.inspectionLoading = false
      }
    },
    onInspectionPageSizeChange (e) {
      const newSize = parseInt(e.target.value)
      this.runInspection(1, newSize)
    },
    onInspectionPageJump (e) {
      const newPage = parseInt(e.target.value)
      this.runInspection(newPage, this.inspectionPageSize)
    },
    getGroupPolylines (group) {
      // Return all summary_polylines in the group
      return group
        .map(act => act.map && act.map.summary_polyline)
        .filter(Boolean)
    },
    // --- Add this method for transposed inspection table ---
    getTransposedInspectionRows (group) {
      const fields = [
        { key: 'team', label: 'Team Name' },
        { key: 'name', label: 'Activity Name' },
        { key: 'pace', label: 'Pace' },
        { key: 'distance', label: 'Distance' },
        { key: 'moving_time', label: 'Moving Time' },
        { key: 'elapsed_time', label: 'Elapsed Time' },
        { key: 'average_heartrate', label: 'AVG HR' },
        { key: 'average_watts', label: 'AVG Watts' },
        { key: 'elev_high', label: 'Elev High' },
        { key: 'elev_low', label: 'Elev Low' },
        { key: 'average_cadence', label: 'AVG Cadence' }
      ]
      return fields.map(field => {
        return [
          field.label,
          ...group.map(act => {
            if (field.key === 'pace') {
              if (act.pace) return act.pace
              if (act.moving_time && act.distance) {
                return act.distance > 0
                  ? (act.moving_time / 60 / (act.distance / 1000)).toFixed(2)
                  : '-'
              }
              return '-'
            }
            if ([
              'average_heartrate',
              'average_watts',
              'elev_high',
              'elev_low'
            ].includes(field.key)) {
              return act[field.key] != null ? act[field.key] : '-'
            }
            return act[field.key] || '-'
          })
        ]
      })
    },
    polylinesToSVGPoints (polylines, groupIdx, width = 300, height = 150, padding = 10) {
      // Decode all polylines and normalize them to the same SVG space, then apply pan/zoom
      const decoded = polylines.map(encoded => polyline.decode(encoded))
      // Flatten all points to find global bounds
      const allPoints = decoded.flat()
      if (!allPoints.length) return []
      let minX = Math.min(...allPoints.map(p => p[1]))
      let maxX = Math.max(...allPoints.map(p => p[1]))
      let minY = Math.min(...allPoints.map(p => p[0]))
      let maxY = Math.max(...allPoints.map(p => p[0]))
      const scaleX = (width - 2 * padding) / (maxX - minX || 1)
      const scaleY = (height - 2 * padding) / (maxY - minY || 1)
      const scale = this.zoom[groupIdx] || 1
      const panX = (this.pan[groupIdx] && this.pan[groupIdx].x) || 0
      const panY = (this.pan[groupIdx] && this.pan[groupIdx].y) || 0
      return decoded.map(points =>
        points.map(p => {
          let x = (p[1] - minX) * scaleX + padding
          let y = height - ((p[0] - minY) * scaleY + padding)
          // Apply zoom and pan
          x = (x - width / 2) * scale + width / 2 + panX
          y = (y - height / 2) * scale + height / 2 + panY
          return `${x},${y}`
        }).join(' ')
      )
    },
    polylineLengths (pointsStr) {
      // Estimate total length for animation
      if (!pointsStr) return 1000
      const pts = pointsStr.split(' ').map(pt => pt.split(',').map(Number))
      let len = 0
      for (let i = 1; i < pts.length; i++) {
        const dx = pts[i][0] - pts[i - 1][0]
        const dy = pts[i][1] - pts[i - 1][1]
        len += Math.sqrt(dx * dx + dy * dy)
      }
      return len
    },
    // --- Interactivity ---
    startPan (e, groupIdx) {
      this.pan[groupIdx] = this.pan[groupIdx] || { x: 0, y: 0, dragging: false, lastX: 0, lastY: 0 }
      this.pan[groupIdx].dragging = true
      this.pan[groupIdx].lastX = e.clientX
      this.pan[groupIdx].lastY = e.clientY
    },
    onMouseMove (e, groupIdx) {
      if (this.pan[groupIdx] && this.pan[groupIdx].dragging) {
        const dx = e.clientX - this.pan[groupIdx].lastX
        const dy = e.clientY - this.pan[groupIdx].lastY
        this.pan[groupIdx].x += dx
        this.pan[groupIdx].y += dy
        this.pan[groupIdx].lastX = e.clientX
        this.pan[groupIdx].lastY = e.clientY
      }
    },
    endPan (groupIdx) {
      if (this.pan[groupIdx]) this.pan[groupIdx].dragging = false
    },
    onWheel (e, groupIdx) {
      const delta = e.deltaY < 0 ? 1.1 : 0.9
      this.zoom[groupIdx] = (this.zoom[groupIdx] || 1) * delta
      if (this.zoom[groupIdx] < 0.5) this.zoom[groupIdx] = 0.5
      if (this.zoom[groupIdx] > 10) this.zoom[groupIdx] = 10
    },
    showTooltip (evt, group, pidx, groupIdx) {
      const act = group[pidx]
      this.tooltip[groupIdx] = {
        visible: true,
        x: evt.offsetX + 10,
        y: evt.offsetY - 10,
        athleteId: act.athlete_id,
        distance: act.distance ? (act.distance / 1000).toFixed(2) : 'N/A'
      }
      this.hoveredPolyline[groupIdx] = pidx
    },
    moveTooltip (evt, groupIdx) {
      if (this.tooltip[groupIdx] && this.tooltip[groupIdx].visible) {
        this.tooltip[groupIdx].x = evt.offsetX + 10
        this.tooltip[groupIdx].y = evt.offsetY - 10
      }
    },
    hideTooltip (groupIdx) {
      if (this.tooltip[groupIdx]) this.tooltip[groupIdx].visible = false
      this.hoveredPolyline[groupIdx] = null
    },
    hasActivityValue (value) {
      if (value === null || value === undefined) return false
      if (typeof value === 'object') {
        return (value.point > 0 || value.km > 0)
      }
      return value > 0
    },
    formatMovingTime (seconds) {
      if (!seconds || seconds === 0) return ''
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const remainingSeconds = Math.floor(seconds % 60)

      if (hours > 0) {
        return `${hours}h ${minutes}m ${remainingSeconds}s`
      } else if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`
      } else {
        return `${remainingSeconds}s`
      }
    },
    getDisplayValue (value, type = 'point') {
      if (!value || typeof value !== 'object') return value

      // Priority: point > km for display
      if (type === 'point' && value.point > 0) return value.point
      if (type === 'km' && value.km > 0) return value.km
      if (value.point > 0) return value.point
      if (value.km > 0) return value.km
      return 0
    }
  }
}
</script>

<style scoped>
.event-var-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Loading State */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-spinner {
  text-align: center;
  color: #4a90e2;
}

.spinner-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

/* Error State */
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.error-card {
  text-align: center;
  padding: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-width: 500px;
}

.error-icon {
  font-size: 3rem;
  color: #e74c3c;
  margin-bottom: 1rem;
}

.retry-btn {
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  transition: background 0.3s;
}

.retry-btn:hover {
  background: #357abd;
}

/* Header Section */
.header-section {
  margin-bottom: 1.5rem;
}

.header-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 1.5rem;
  color: white;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.event-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.title-icon {
  font-size: 1.5rem;
}

.event-meta {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  font-size: 0.9rem;
  opacity: 0.9;
}

.meta-icon {
  margin-right: 0.25rem;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.export-btn, .refresh-btn, .team-dashboard-btn {
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}

.export-btn:hover, .refresh-btn:hover, .team-dashboard-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* Filters Section */
.filters-section {
  margin-bottom: 1.5rem;
}

.filters-card {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.filters-content {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.search-wrapper {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 2px solid #e1e8ed;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: border-color 0.3s;
}

.search-input:focus {
  outline: none;
  border-color: #4a90e2;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #7f8c8d;
}

.filter-wrapper {
  min-width: 150px;
}

.team-select, .gender-select, .activity-type-select {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e1e8ed;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s;
}

.team-select:focus, .gender-select:focus, .activity-type-select:focus {
  outline: none;
  border-color: #4a90e2;
}

.clear-filters-btn {
  padding: 0.75rem 1rem;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.clear-filters-btn:hover {
  background: #c0392b;
}

/* Table Section */
.table-section {
  margin-bottom: 2rem;
}

.table-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.table-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e1e8ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.table-title {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.table-icon {
  color: #4a90e2;
}

.table-info {
  font-size: 0.9rem;
  color: #7f8c8d;
}

/* Desktop Table */
.desktop-table {
  overflow-x: auto;
}

.stats-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.stats-table th {
  background: #f8f9fa;
  padding: 1rem 0.75rem;
  text-align: center;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e1e8ed;
  white-space: nowrap;
}

.stats-table td {
  padding: 0.75rem;
  border-bottom: 1px solid #e1e8ed;
  vertical-align: middle;
}

.data-row:hover {
  background: #f8f9fa;
}

.rank-cell {
  font-weight: 600;
  color: #4a90e2;
  text-align: center;
  width: 30px;
}

.athlete-cell {
  min-width: 100px;
}

.athlete-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.athlete-name {
  font-weight: 600;
  color: #2c3e50;
}

.athlete-name-link {
  color: #4a90e2;
  text-decoration: none;
  transition: color 0.3s;
}

.athlete-name-link:hover {
  color: #357abd;
  text-decoration: underline;
}

.athlete-id {
  font-size: 0.8rem;
  color: #7f8c8d;
}

.gender-cell {
  width: 40px;
}

.gender-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.gender-badge.M {
  background: #e3f2fd;
  color: #1976d2;
}

.gender-badge.F {
  background: #fce4ec;
  color: #c2185b;
}

.team-cell {
  min-width: 60px;
}

.team-badge {
  padding: 0.25rem 0.5rem;
  background: #e8f5e8;
  color: #2e7d32;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
}
.team-icon {
  color: #2e7d32;
  font-size: 1em;
  margin-right: 0.2em;
}

.no-team {
  color: #7f8c8d;
  font-style: italic;
}

.date-cell {
  text-align: center;
  min-width: 40px;
}

.date-cell.has-activity {
  background: #e8f5e8;
}

/* Multi-type event styles */
.date-group-header {
  text-align: center;
  font-weight: 600;
  background: #f8f9fa;
  border-right: 1px solid #e1e8ed;
  padding: 0.75rem 0.5rem;
  font-size: 0.9rem;
  color: #2c3e50;
}

.activity-type-header {
  text-align: center;
  font-weight: 500;
  border-right: 1px solid #e1e8ed;
  padding: 0.5rem 0.25rem;
  font-size: 0.8rem;
  color: #5f6368;
  min-width: 50px;
  position: relative;
}

.activity-type-header[data-type="Swim"] {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1976d2;
}

.activity-type-header[data-type="Ride"] {
  background: linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%);
  color: #f57c00;
}

.activity-type-header[data-type="Run"] {
  background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
  color: #2e7d32;
}

.activity-type-header[data-type="Core"] {
  background: linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%);
  color: #c2185b;
}

.activity-type-cell {
  min-width: 50px;
  font-size: 0.85rem;
  padding: 0.5rem 0.25rem;
}

.activity-type-cell.has-activity {
  background: #e8f5e8;
  font-weight: 500;
}

/* Activity data display styles */
.activity-data {
  text-align: center;
  line-height: 1.2;
}

.main-value {
  font-weight: 600;
  color: #2e7d32;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.unit {
  font-size: 0.7rem;
  color: #666;
  font-weight: 400;
}

.additional-info {
  margin-top: 2px;
}

.km-info {
  font-size: 0.7rem;
  color: #1976d2;
  font-weight: 500;
}

.time-info {
  font-size: 0.75rem;
  color: #666;
  font-weight: 400;
}

/* Mobile activity data styles */
.activity-data-mobile {
  text-align: center;
  line-height: 1.2;
}

.main-value-mobile {
  font-weight: 600;
  color: #2e7d32;
  font-size: 0.85rem;
}

.km-info-mobile {
  font-size: 0.65rem;
  color: #1976d2;
  font-weight: 500;
  margin-top: 1px;
}

.time-info-mobile {
  font-size: 0.7rem;
  color: #666;
  font-weight: 400;
  margin-top: 1px;
}

.km-value {
  font-weight: 600;
  color: #2e7d32;
}

.no-activity {
  color: #7f8c8d;
  font-style: italic;
}

/* Mobile Cards */
.mobile-cards {
  display: none;
  padding: 1rem;
  gap: 1rem;
}

.athlete-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem;
  border: 1px solid #e1e8ed;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.athlete-meta {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.card-content {
  padding: 0.75rem;
  font-size: 0.9rem;
}

.activities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 1rem;
}

/* Multi-type mobile styles */
.date-group-mobile {
  margin-bottom: 1.2rem;
  border: 1px solid #e1e8ed;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  background: white;
}

.date-header-mobile {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0.8rem;
  font-weight: 700;
  text-align: center;
  font-size: 1rem;
  color: white;
  border-bottom: none;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.activity-types-mobile {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  gap: 0.5rem;
  padding: 0.5rem;
}

.activity-type-item {
  text-align: center;
  padding: 0.4rem 0.2rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #e1e8ed;
  min-height: 2.2em;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.activity-type-item.has-activity {
  background: #e8f5e8;
  border-color: #4caf50;
  font-size: 1.1rem;
  padding: 0.5rem 0.2rem;
}

.activity-type-label {
  display: block;
  font-size: 0.7rem;
  color: #7f8c8d;
  margin-bottom: 0.2rem;
  font-weight: 500;
}

.activity-item {
  padding: 0.4rem;
  background: white;
  border-radius: 6px;
  text-align: center;
  border: 1px solid #e1e8ed;
}

.activity-item.has-activity {
  background: #e8f5e8;
  border-color: #4caf50;
  font-size: 1.1rem;
  padding: 0.5rem 0.2rem;
  min-height: 2.2em;
}

.date-label {
  display: block;
  font-size: 0.75rem;
  color: #7f8c8d;
  margin-bottom: 0.2rem;
}

/* Pagination */
.pagination-section {
  padding: 1.5rem;
  border-top: 1px solid #e1e8ed;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-btn:hover:not(:disabled) {
  background: #357abd;
}

.pagination-btn:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
}

.page-btn {
  padding: 0.5rem 0.75rem;
  background: white;
  color: #4a90e2;
  border: 1px solid #4a90e2;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s;
  min-width: 40px;
}

.page-btn:hover {
  background: #e3f0fc;
}

.page-btn.active {
  background: #4a90e2;
  color: white;
}

/* Popup Styles */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4); /* slightly less dark */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 0.5rem; /* less padding */
}

.popup-content {
  background: white;
  border-radius: 10px;
  max-width: 340px; /* smaller max width */
  width: 100%;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  animation: popupSlideIn 0.2s ease-out;
  font-size: 0.95rem; /* slightly smaller font */
}

@keyframes popupSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.popup-header {
  padding: 0.75rem 1rem 0.5rem;
  border-bottom: 1px solid #e1e8ed;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
}

.popup-icon {
  font-size: 1.1rem;
  color: #4a90e2;
}

.popup-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  flex: 1;
}

.popup-close-btn {
  background: none;
  border: none;
  font-size: 1rem;
  color: #7f8c8d;
  cursor: pointer;
  padding: 0.3rem;
  border-radius: 50%;
  transition: all 0.2s;
}

.popup-close-btn:hover {
  background: #f8f9fa;
  color: #2c3e50;
}

.popup-body {
  padding: 0.75rem 1rem;
}

.popup-body p {
  margin: 0 0 0.75rem 0;
  color: #2c3e50;
  line-height: 1.4;
  font-size: 0.95em;
}

.popup-features {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 0.92em;
}

.feature-icon {
  color: #4a90e2;
  font-size: 1em;
  width: 16px;
}

.popup-footer {
  padding: 0.5rem 1rem 1rem;
  border-top: 1px solid #e1e8ed;
  text-align: center;
}

.popup-continue-btn {
  padding: 0.5rem 1.2rem;
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  transition: background 0.2s;
}

.popup-continue-btn:hover {
  background: #357abd;
}

/* Inspection Results Section */
.inspection-section {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.inspection-title {
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.inspection-icon {
  color: #4a90e2;
  font-size: 1.1rem;
}

.inspection-group h3 {
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #34495e;
  border-bottom: 1px solid #e1e8ed;
  padding-bottom: 0.5rem;
}

.inspection-similar-set {
  margin-bottom: 1.5rem;
  border: 1px solid #e1e8ed;
  border-radius: 8px;
  overflow: hidden;
}

.inspection-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.inspection-table th {
  background: #f8f9fa;
  padding: 0.75rem 0.5rem;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 1px solid #e1e8ed;
  white-space: nowrap;
}

.inspection-table td {
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid #e1e8ed;
  vertical-align: middle;
}

.inspection-table tr:last-child td {
  border-bottom: none;
}

.inspection-close-btn {
  background: none;
  border: none;
  font-size: 2rem;
  color: #888;
  cursor: pointer;
  margin-left: 1rem;
  line-height: 1;
  padding: 0 0.5rem;
  border-radius: 50%;
  transition: background 0.2s;
}
.inspection-close-btn:hover {
  background: #eee;
  color: #222;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-content {
  background: #fff;
  border-radius: 10px;
  padding: 2rem;
  max-width: 900px;
  width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.inspection-modal-content {
  max-width: 1400px;
  padding: 2.5rem 2rem 2rem 2rem;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  background: #fff;
}
.inspection-title {
  margin: 0 0 0.2rem 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.inspection-subtitle {
  font-size: 1.02rem;
  color: #666;
  margin-bottom: 0.5rem;
  margin-top: 0.2rem;
}
.inspection-close-btn.improved {
  font-size: 2.2rem;
  color: #e74c3c;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  padding: 0.2em 0.6em;
  transition: background 0.2s, color 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
.inspection-close-btn.improved:hover {
  background: #fbeee6;
  color: #b71c1c;
}
.improved-group {
  margin-bottom: 2.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e1e8ed;
}
.inspection-group-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #34495e;
  margin-bottom: 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.7rem;
}
.group-size {
  font-size: 0.98em;
  color: #888;
  font-weight: 400;
}
.improved-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.97rem;
  margin-top: 0.5rem;
  background: #fff;
}
.improved-table thead th {
  position: sticky;
  top: 0;
  background: #f8f9fa;
  z-index: 2;
  padding: 0.85rem 0.5rem;
  text-align: left;
  font-weight: 700;
  color: #2c3e50;
  border-bottom: 2px solid #e1e8ed;
  white-space: nowrap;
}
.improved-table tbody tr:nth-child(even) {
  background: #f4f8fb;
}
.improved-table tbody tr:nth-child(odd) {
  background: #fff;
}
.improved-table td {
  padding: 0.7rem 0.5rem;
  border-bottom: 1px solid #e1e8ed;
  vertical-align: middle;
}
.improved-table tr:last-child td {
  border-bottom: none;
}
.highlight-row {
  background: #fffbe6 !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .event-var-container {
    padding: 0.8rem;
    background: #f8f9fa;
    min-height: 100vh;
  }

  .header-section {
    margin-bottom: 1rem;
  }

  .header-card {
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .event-title {
    font-size: 1.3rem;
    margin-bottom: 0.25rem;
  }

  .title-icon {
    font-size: 1.2rem;
  }

  .event-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    font-size: 0.8rem;
  }

  .header-actions {
    gap: 2rem;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }
  .export-btn, .refresh-btn, .team-dashboard-btn {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
    min-width: 44px;
    min-height: 44px;
    border-radius: 8px;
    line-height: 1.2;
    gap: 0.4rem;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
  }

  .export-btn:hover, .refresh-btn:hover, .team-dashboard-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  .export-btn .fa, .refresh-btn .fa, .team-dashboard-btn .fa {
    font-size: 1em;
  }
  /* Hide button text in header-actions, show only icons */
  .header-actions .export-btn > span,
  .header-actions .refresh-btn > span,
  .header-actions .team-dashboard-btn > span,
  .header-actions .export-btn > svg + span,
  .header-actions .refresh-btn > svg + span,
  .header-actions .team-dashboard-btn > svg + span {
    display: none !important;
  }

  .filters-section {
    margin-bottom: 1rem;
  }

  .filters-card {
    padding: 1.2rem;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  }

  .filters-content {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .search-wrapper {
    min-width: auto;
  }

  .search-input {
    padding: 0.8rem 1rem 0.8rem 2.5rem;
    font-size: 1rem;
    border-radius: 8px;
    border: 2px solid #e1e8ed;
    transition: border-color 0.3s;
  }

  .search-input:focus {
    border-color: #4a90e2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
  }

  .search-icon {
    left: 0.8rem;
    font-size: 1rem;
    color: #7f8c8d;
  }

  .team-select, .gender-select, .activity-type-select {
    padding: 0.8rem;
    font-size: 1rem;
    border-radius: 8px;
    border: 2px solid #e1e8ed;
    transition: border-color 0.3s;
    min-height: 44px;
  }

  .team-select:focus, .gender-select:focus, .activity-type-select:focus {
    border-color: #4a90e2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
  }

  .clear-filters-btn {
    padding: 0.8rem 1.2rem;
    font-size: 1rem;
    border-radius: 8px;
    min-height: 44px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
  }

  .clear-filters-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: flex;
    flex-direction: column;
  }

  .athlete-card {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    background: white;
    border: 1px solid #f0f0f0;
  }

  .card-header {
    flex-direction: row;
    align-items: center;
    gap: 0.8rem;
    margin-bottom: 1rem;
    padding-bottom: 0.8rem;
    border-bottom: 1px solid #f0f0f0;
  }
  .athlete-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
    flex: 1;
  }
  .athlete-name {
    font-size: 1.1rem;
    margin: 0;
    font-weight: 700;
    color: #2c3e50;
  }
  .athlete-id {
    font-size: 0.8rem;
    color: #7f8c8d;
    background: #f8f9fa;
    padding: 0.2rem 0.5rem;
    border-radius: 12px;
  }
  .athlete-meta {
    flex-direction: row;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
  }
  .gender-badge, .team-badge {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
    border-radius: 16px;
    font-weight: 600;
  }
  .activities-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.8rem;
  }

  .activity-types-mobile {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
    gap: 0.6rem;
    padding: 0.8rem;
  }

  .activity-type-item {
    padding: 0.6rem 0.4rem;
    font-size: 0.9rem;
    min-height: 3.5em;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .activity-type-item:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .activity-type-label {
    font-size: 0.75rem;
    margin-bottom: 0.3rem;
    font-weight: 600;
    color: #5f6368;
  }

  .main-value-mobile {
    font-size: 1rem;
    font-weight: 700;
    color: #2e7d32;
    margin-bottom: 0.2rem;
  }

  .km-info-mobile {
    font-size: 0.8rem;
    margin-top: 0.2rem;
    color: #1976d2;
    font-weight: 500;
  }

  .time-info-mobile {
    font-size: 0.75rem;
    margin-top: 0.2rem;
    color: #666;
    font-weight: 400;
  }

  .activity-item {
    padding: 0.6rem 0.4rem;
    font-size: 0.9rem;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .activity-item:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .date-label {
    font-size: 0.75rem;
    margin-bottom: 0.3rem;
    font-weight: 600;
    color: #5f6368;
  }

  .km-value {
    font-size: 0.9rem;
    font-weight: 600;
    color: #2e7d32;
  }

  .card-content {
    padding: 0;
    font-size: 0.9rem;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 1.2rem;
    padding: 1rem;
  }

  .page-numbers {
    order: -1;
    display: flex;
    justify-content: center;
    gap: 0.4rem;
    flex-wrap: wrap;
  }

  .pagination-btn {
    padding: 0.8rem 1.2rem;
    font-size: 1rem;
    min-width: 44px;
    min-height: 44px;
    border-radius: 8px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .page-btn {
    padding: 0.8rem 1rem;
    font-size: 1rem;
    min-width: 44px;
    min-height: 44px;
    border-radius: 8px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  .mobile-medal-icon {
    display: inline-block;
    color: #ffd700;
    font-size: 1.1em;
    margin-left: 0.3em;
    vertical-align: middle;
  }
  .popup-content {
    max-width: 95vw;
    font-size: 0.9rem;
  }
  .popup-header {
    padding: 0.5rem 0.7rem 0.3rem;
    gap: 0.3rem;
  }
  .popup-body {
    padding: 0.5rem 0.7rem;
  }
  .popup-footer {
    padding: 0.4rem 0.7rem 0.7rem;
  }
  .feature-item {
    padding: 0.25rem 0.3rem;
    font-size: 0.85em;
    gap: 0.3rem;
  }
  .feature-icon {
    font-size: 0.9em;
    width: 14px;
  }
  .popup-continue-btn {
    padding: 0.4rem 1rem;
    font-size: 0.9rem;
  }
  .table-card {
    width: 100vw;
    margin-left: calc(-1 * (100vw - 100%) / 2);
    margin-right: calc(-1 * (100vw - 100%) / 2);
    border-radius: 0;
    padding-left: 0;
    padding-right: 0;
  }
  .inspection-section {
    margin-top: 1rem;
    padding: 0.75rem;
  }
  .inspection-title {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
  .inspection-icon {
    font-size: 1em;
  }
  .inspection-group h3 {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
  .inspection-similar-set {
    margin-bottom: 1rem;
  }
  .inspection-table {
    font-size: 0.75rem;
  }
  .inspection-table th {
    padding: 0.5rem 0.3rem;
  }
  .inspection-table td {
    padding: 0.5rem 0.3rem;
  }
  .inspection-modal-content {
    padding: 1.2rem 0.3rem 1.2rem 0.3rem;
    max-width: 99vw;
  }
  .improved-table thead th, .improved-table td {
    font-size: 0.85rem;
    padding: 0.5rem 0.2rem;
  }
  .inspection-title {
    font-size: 1.1rem;
  }
  .inspection-group-title {
    font-size: 0.95rem;
  }
  .inspection-modal-content,
  .modal-content,
  .compact-inspection-modal {
    max-width: 100vw !important;
    width: 100vw !important;
    min-width: 100vw !important;
    height: 100vh !important;
    min-height: 100vh !important;
    border-radius: 0 !important;
    padding: 0 !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    position: fixed !important;
    overflow-y: auto !important;
    box-sizing: border-box !important;
  }
  .modal-overlay {
    padding: 0 !important;
  }
}
/* --- Compact Inspection Modal Improvements --- */
.compact-inspection-modal {
  padding: 1.2rem 0.7rem 1.2rem 0.7rem !important;
  max-width: 1400px !important;
}
.inspection-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.2rem;
  gap: 0.5rem;
}
.compact-title {
  font-size: 1.05rem !important;
  margin: 0;
  font-weight: 700;
}
.compact-close {
  font-size: 1.5rem !important;
  padding: 0.1em 0.4em !important;
  margin-left: 0.5rem;
}
.compact-subtitle {
  font-size: 0.92rem !important;
  margin-bottom: 0.5rem !important;
  margin-top: 0.1rem !important;
}
.compact-group {
  margin-bottom: 1.2rem !important;
  padding-bottom: 0.7rem !important;
  border-bottom: 1px solid #e1e8ed;
}
.compact-group-flex {
  display: flex;
  gap: 1.2rem;
  align-items: flex-start;
  flex-wrap: wrap;
}
.compact-svg-legend {
  min-width: 250px;
  max-width: 350px;
  width: 100%;
  position: relative;
  margin-bottom: 0.2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.compact-svg {
  height: 220px !important;
  width: 100% !important;
  max-width: 100%;
}
.compact-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-bottom: 0.2rem;
  margin-top: 0.1rem;
}
.compact-legend-item {
  font-size: 0.88em;
  display: flex;
  align-items: center;
  margin-right: 0.5em;
}
.compact-group-title-table {
  flex: 1;
  min-width: 0;
  /* Ensure long titles wrap and are fully visible */
  white-space: normal;
  overflow: visible;
}
.compact-group-title {
  font-size: 1.01rem !important;
  margin-bottom: 0.2rem !important;
  margin-top: 0.1rem !important;
  /* Allow full text display for long titles */
  white-space: normal;
  word-break: break-word;
}
.compact-table {
  font-size: 0.92em !important;
  margin-top: 0.1rem !important;
}
.compact-table th, .compact-table td {
  padding: 0.35rem 0.3rem !important;
  font-size: 0.92em !important;
  white-space: nowrap;
}
.compact-table th {
  font-weight: 600;
}
.compact-table td {
  font-weight: 400;
}
.compact-header {
  white-space: normal;
  word-break: break-word;
  font-size: 0.92em;
  padding: 0.4rem 0.2rem !important;
  text-align: center;
  vertical-align: bottom;
  line-height: 1.1;
  min-width: 40px;
  max-width: 90px;
}
.improved-table th.compact-header {
  font-size: 0.88em !important;
}
@media (max-width: 900px) {
  .compact-group-flex {
    flex-direction: column;
    gap: 0.5rem;
  }
  .compact-svg-legend {
    max-width: 100vw;
    min-width: 0;
  }
}
.impressive-svg {
  box-shadow: 0 8px 32px rgba(25,118,210,0.13), 0 1.5px 8px rgba(60,60,120,0.08);
  border: 1.5px solid #1976d2;
  background: none;
  margin-bottom: 0.5rem;
  transition: box-shadow 0.2s;
}
.impressive-svg:hover {
  box-shadow: 0 12px 40px rgba(25,118,210,0.18), 0 2px 12px rgba(60,60,120,0.12);
}
.vertical-table th,
.vertical-table td {
  text-align: center;
  vertical-align: middle;
}
.vertical-table th:first-child {
  text-align: left;
  background: #f8f9fa;
  font-weight: 600;
}
</style>

@keyframes draw {
  from { stroke-dashoffset: 1000; }
  to { stroke-dashoffset: 0; }
}
